import React, { useEffect, useMemo, useState } from 'react';
import aulaDocenteService from '../../services/AulaDocenteService';
import './AulaStatsModal.css';

const pad = (value) => String(value).padStart(2, '0');

const getTodayDate = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const shiftDate = (value, days) => {
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const AulaDocenteStatsModal = ({ open, aula, onClose }) => {
  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(getTodayDate());
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const aulaId = aula?.id || aula?.Id || null;
  const aulaNombre = aula?.nombre || aula?.Nombre || 'Aula docente';

  const loadStats = async (nextFrom = fromDate, nextTo = toDate) => {
    if (!aulaId) return;

    setLoading(true);
    setError('');
    try {
      const response = await aulaDocenteService.getAulaDocenteStats(aulaId, {
        from: nextFrom,
        to: nextTo
      });
      setStats(response || null);
    } catch (err) {
      setStats(null);
      setError(err.message || 'No pudimos cargar las estadísticas del aula docente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !aulaId) return;
    const today = getTodayDate();
    setFromDate(today);
    setToDate(today);
    void loadStats(today, today);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, aulaId]);

  const currentSummary = useMemo(() => stats?.resumen?.estadoActual || {}, [stats]);
  const rangeSummary = useMemo(() => stats?.resumen?.actividadEnRango || {}, [stats]);
  const presencia = useMemo(() => stats?.presencia || {}, [stats]);
  const assistantsTop = Array.isArray(stats?.asistentes?.top) ? stats.asistentes.top : [];
  const assistantsCatalog = Array.isArray(stats?.asistentes?.catalogo) ? stats.asistentes.catalogo : [];
  const recentActivity = Array.isArray(stats?.actividadReciente) ? stats.actividadReciente : [];

  const currentCards = useMemo(() => ([
    { label: 'Docentes', value: currentSummary.totalDocentes ?? 0, icon: 'fa-user-tie' },
    { label: 'Asistentes', value: currentSummary.totalAsistentes ?? 0, icon: 'fa-robot' },
    { label: 'Archivos', value: currentSummary.totalArchivos ?? 0, icon: 'fa-folder-open' },
    { label: 'Links', value: currentSummary.totalLinks ?? 0, icon: 'fa-link' },
    { label: 'Mensajes', value: currentSummary.totalMensajesForo ?? 0, icon: 'fa-comments' },
    { label: 'Hilos', value: currentSummary.totalHilosForo ?? 0, icon: 'fa-stream' },
    { label: 'Respuestas', value: currentSummary.totalRespuestasForo ?? 0, icon: 'fa-reply' },
    { label: 'Chat ahora', value: presencia?.chat?.activos ?? 0, icon: 'fa-user-friends' },
    { label: 'Pizarra ahora', value: presencia?.pizarra?.activos ?? 0, icon: 'fa-chalkboard' },
    { label: 'Videollamada', value: presencia?.videollamada?.activa ? 'Activa' : 'No', icon: 'fa-video' }
  ]), [currentSummary, presencia]);

  const rangeCards = useMemo(() => ([
    { label: 'Ingresos', value: rangeSummary.visitasAula ?? 0, icon: 'fa-eye' },
    { label: 'Asistentes abiertos', value: rangeSummary.aperturasAsistentes ?? 0, icon: 'fa-robot' },
    { label: 'Chats iniciados', value: rangeSummary.chatsIniciados ?? 0, icon: 'fa-comments' },
    { label: 'Ingresos al chat', value: rangeSummary.participacionesEnChat ?? 0, icon: 'fa-user-friends' },
    { label: 'Archivos subidos', value: rangeSummary.archivosSubidos ?? 0, icon: 'fa-upload' },
    { label: 'Descargas', value: rangeSummary.archivosDescargados ?? 0, icon: 'fa-download' },
    { label: 'Links creados', value: rangeSummary.linksCreados ?? 0, icon: 'fa-plus-circle' },
    { label: 'Links abiertos', value: rangeSummary.linksAbiertos ?? 0, icon: 'fa-external-link-alt' },
    { label: 'Mensajes foro', value: rangeSummary.mensajesForo ?? 0, icon: 'fa-comment-dots' },
    { label: 'Hilos foro', value: rangeSummary.hilosForo ?? 0, icon: 'fa-stream' },
    { label: 'Respuestas foro', value: rangeSummary.respuestasForo ?? 0, icon: 'fa-reply' },
    { label: 'Pizarras', value: rangeSummary.pizarrasIniciadas ?? 0, icon: 'fa-chalkboard' },
    { label: 'Ingresos a pizarra', value: rangeSummary.participacionesEnPizarra ?? 0, icon: 'fa-user-edit' },
    { label: 'Videollamadas', value: rangeSummary.videollamadasIniciadas ?? 0, icon: 'fa-video' },
    { label: 'Ingresos a llamadas', value: rangeSummary.participacionesEnVideollamada ?? 0, icon: 'fa-phone-volume' },
    { label: 'Docentes activos', value: rangeSummary.docentesConActividad ?? 0, icon: 'fa-user-check' }
  ]), [rangeSummary]);

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-card aula-stats-modal aula-docente-stats-modal"
        style={{ maxWidth: '1040px', width: 'min(1040px, 96vw)' }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-header aula-stats-modal-header">
          <div>
            <h3><i className="fas fa-chart-line" /> Estadísticas del aula docente</h3>
            <p>{aulaNombre}</p>
          </div>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="modal-body aula-stats-modal-body">
          <div className="aula-stats-toolbar">
            <div className="aula-stats-filters">
              <label>
                <span>Desde</span>
                <input type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
              </label>
              <label>
                <span>Hasta</span>
                <input type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} />
              </label>
            </div>
            <div className="aula-stats-toolbar-actions">
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => {
                  const today = getTodayDate();
                  setFromDate(today);
                  setToDate(today);
                  void loadStats(today, today);
                }}
              >
                Hoy
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => {
                  const today = getTodayDate();
                  const from = shiftDate(today, -6);
                  setFromDate(from);
                  setToDate(today);
                  void loadStats(from, today);
                }}
              >
                Últimos 7 días
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => {
                  const today = getTodayDate();
                  const from = shiftDate(today, -29);
                  setFromDate(from);
                  setToDate(today);
                  void loadStats(from, today);
                }}
              >
                Últimos 30 días
              </button>
              <button className="btn btn-primary" type="button" onClick={() => loadStats()} disabled={loading}>
                <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-filter'}`} />
                Aplicar
              </button>
            </div>
          </div>

          {loading && (
            <div className="data-table-loading">
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin" />
              </div>
              <p>Cargando estadísticas del aula docente...</p>
            </div>
          )}

          {!loading && error && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-exclamation-triangle" />
              </div>
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && stats && (
            <>
              <section className="aula-stats-section">
                <div className="aula-stats-section-header">
                  <div>
                    <h4>Estado actual</h4>
                    <p>Recursos y participación acumulada del aula docente.</p>
                  </div>
                </div>
                <div className="aula-stats-grid">
                  {currentCards.map((card) => (
                    <article key={card.label} className="aula-stats-card">
                      <div className="aula-stats-card-icon">
                        <i className={`fas ${card.icon}`} />
                      </div>
                      <div>
                        <p>{card.label}</p>
                        <strong>{card.value}</strong>
                      </div>
                    </article>
                  ))}
                </div>
                <div className="aula-stats-meta-grid">
                  <div className="aula-stats-meta-card">
                    <span>Último archivo subido</span>
                    <strong>{formatDateTime(currentSummary.ultimoArchivoSubido)}</strong>
                  </div>
                  <div className="aula-stats-meta-card">
                    <span>Último movimiento en foro</span>
                    <strong>{formatDateTime(currentSummary.ultimoMovimientoForo)}</strong>
                  </div>
                </div>
              </section>

              <section className="aula-stats-section">
                <div className="aula-stats-section-header">
                  <div>
                    <h4>Actividad del rango</h4>
                    <p>Desde {stats?.rango?.from || fromDate} hasta {stats?.rango?.to || toDate}.</p>
                  </div>
                </div>
                <div className="aula-stats-grid">
                  {rangeCards.map((card) => (
                    <article key={card.label} className="aula-stats-card accent">
                      <div className="aula-stats-card-icon">
                        <i className={`fas ${card.icon}`} />
                      </div>
                      <div>
                        <p>{card.label}</p>
                        <strong>{card.value}</strong>
                      </div>
                    </article>
                  ))}
                </div>
              </section>

              <section className="aula-stats-section">
                <div className="aula-stats-section-header">
                  <div>
                    <h4>Presencia en vivo</h4>
                    <p>Conectados ahora en chat, pizarra y estado de la videollamada.</p>
                  </div>
                </div>
                <div className="aula-stats-live-grid">
                  <div className="aula-stats-panel">
                    <div className="aula-stats-panel-title">
                      <i className="fas fa-comments" />
                      <span>Chat docente</span>
                    </div>
                    <p className="aula-stats-panel-caption">Activos: {presencia?.chat?.activos ?? 0}</p>
                    <div className="aula-stats-chip-list">
                      {Array.isArray(presencia?.chat?.usuarios) && presencia.chat.usuarios.length > 0 ? presencia.chat.usuarios.map((item) => (
                        <span key={`${item.userId || item.displayName}-chat`} className="curso-filter-chip">
                          <i className="fas fa-user" />
                          {item.displayName} · {item.roleName || item.rol || 'Usuario'}
                        </span>
                      )) : (
                        <span className="status-chip pending">Sin usuarios conectados</span>
                      )}
                    </div>
                  </div>
                  <div className="aula-stats-panel">
                    <div className="aula-stats-panel-title">
                      <i className="fas fa-chalkboard" />
                      <span>Pizarra colaborativa</span>
                    </div>
                    <p className="aula-stats-panel-caption">Activos: {presencia?.pizarra?.activos ?? 0}</p>
                    <div className="aula-stats-chip-list">
                      {Array.isArray(presencia?.pizarra?.usuarios) && presencia.pizarra.usuarios.length > 0 ? presencia.pizarra.usuarios.map((item) => (
                        <span key={`${item.userId || item.displayName}-board`} className="curso-filter-chip">
                          <i className="fas fa-user-edit" />
                          {item.displayName} · {item.roleName || item.rol || 'Usuario'}
                        </span>
                      )) : (
                        <span className="status-chip pending">Sin usuarios conectados</span>
                      )}
                    </div>
                  </div>
                  <div className="aula-stats-panel">
                    <div className="aula-stats-panel-title">
                      <i className="fas fa-video" />
                      <span>Videollamada</span>
                    </div>
                    <p className="aula-stats-panel-caption">
                      {presencia?.videollamada?.activa
                        ? `Sala activa iniciada por ${presencia.videollamada.iniciadaPor || '—'}`
                        : 'Sin sala activa en este momento'}
                    </p>
                    <div className="aula-stats-activity-list">
                      {Array.isArray(presencia?.videollamada?.eventos) && presencia.videollamada.eventos.length > 0 ? presencia.videollamada.eventos.map((item, index) => (
                        <div key={`${item.tipo}-${item.fechaUtc}-${index}`} className="aula-stats-activity-item">
                          <div>
                            <strong>{item.tipo}</strong>
                            <span>{item.nombre} · {item.rol || 'Usuario'}</span>
                          </div>
                          <time>{formatDateTime(item.fechaUtc)}</time>
                        </div>
                      )) : (
                        <span className="status-chip pending">Sin eventos de videollamada en el rango</span>
                      )}
                    </div>
                  </div>
                </div>
              </section>

              <section className="aula-stats-section">
                <div className="aula-stats-section-header">
                  <div>
                    <h4>Asistentes</h4>
                    <p>Catálogo disponible y ranking de interacción dentro del rango.</p>
                  </div>
                </div>
                <div className="aula-stats-live-grid">
                  <div className="aula-stats-panel">
                    <div className="aula-stats-panel-title">
                      <i className="fas fa-layer-group" />
                      <span>Disponibles</span>
                    </div>
                    <div className="aula-stats-chip-list">
                      {assistantsCatalog.length > 0 ? assistantsCatalog.map((assistant) => (
                        <span key={assistant.assistantId || assistant.nombre} className="curso-filter-chip">
                          <i className="fas fa-brain" />
                          {assistant.nombre} · {assistant.tipo}
                        </span>
                      )) : (
                        <span className="status-chip pending">No hay asistentes asociados</span>
                      )}
                    </div>
                  </div>
                  <div className="aula-stats-panel">
                    <div className="aula-stats-panel-title">
                      <i className="fas fa-fire" />
                      <span>Top interacción</span>
                    </div>
                    <div className="aula-stats-activity-list">
                      {assistantsTop.length > 0 ? assistantsTop.map((assistant) => (
                        <div key={`${assistant.assistantId}-${assistant.nombre}`} className="aula-stats-activity-item">
                          <div>
                            <strong>{assistant.nombre}</strong>
                            <span>{assistant.tipo} · {assistant.totalInteracciones ?? assistant.totalConsultas ?? 0} evento(s)</span>
                          </div>
                          <time>{formatDateTime(assistant.ultimoUsoUtc)}</time>
                        </div>
                      )) : (
                        <span className="status-chip pending">Sin interacción registrada en el rango</span>
                      )}
                    </div>
                  </div>
                </div>
              </section>

              <section className="aula-stats-section">
                <div className="aula-stats-section-header">
                  <div>
                    <h4>Actividad reciente</h4>
                    <p>Últimos eventos registrados para esta aula docente.</p>
                  </div>
                </div>
                <div className="aula-stats-activity-list">
                  {recentActivity.length > 0 ? recentActivity.map((item, index) => (
                    <div key={`${item.evento}-${item.fechaUtc}-${index}`} className="aula-stats-activity-item">
                      <div>
                        <strong>{item.evento}</strong>
                        <span>{item.actor} · {item.rol || 'Usuario'}</span>
                        <small>{item.detalle}</small>
                      </div>
                      <time>{formatDateTime(item.fechaUtc)}</time>
                    </div>
                  )) : (
                    <div className="data-table-empty compact">
                      <p>No hay actividad registrada para este rango.</p>
                    </div>
                  )}
                </div>
              </section>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default AulaDocenteStatsModal;
